import React, { useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { z } from "zod";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { simulateIncomingSms } from "@lib/simulations";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { VStack } from "@design-system/VStack/VStack";

const simulateIncomingSmsFormSchema = z.object({
    From: z
        .string()
        .regex(/^\d+$/, "Must be digits only")
        .min(1, "Number is required"),
    Body: z.string(),
    MediaUrl0: z.union([z.string().url("Must be a valid URL"), z.literal("")]),
    MediaUrl1: z.union([z.string().url("Must be a valid URL"), z.literal("")]),
});

type SimulateIncomingSmsFormValues = z.infer<
    typeof simulateIncomingSmsFormSchema
>;

const fields = createFormikFields<SimulateIncomingSmsFormValues>();

export const SimulateIncomingSms: React.FC = () => {
    const [loading, setLoading] = useState(false);

    const formik = useFormik<SimulateIncomingSmsFormValues>({
        initialValues: {
            From: "",
            Body: "",
            MediaUrl0: "",
            MediaUrl1: "",
        },
        validate: validateFormikWithZodSchema(simulateIncomingSmsFormSchema),
        onSubmit: submitHelper({
            loading,
            setLoading,
            handler: async (body, { resetForm }) => {
                const numberOfAttachments: number = [
                    body.MediaUrl0,
                    body.MediaUrl1,
                ].filter(Boolean).length;

                await simulateIncomingSms({
                    ...body,
                    NumMedia: numberOfAttachments,
                });

                resetForm();
            },
        }),
    });

    return (
        <section>
            <h3>Simulate Incoming SMS</h3>

            <FormikProvider value={formik}>
                <Form>
                    <VStack gap={16} align="start">
                        <fields.Text name="From" label="From" size="xl" />
                        <fields.Text name="Body" label="Body" size="xl" />
                        <fields.Text
                            name="MediaUrl0"
                            label="Attachment URL"
                            size="xl"
                        />
                        <fields.Text
                            name="MediaUrl1"
                            label="Attachment URL"
                            size="xl"
                        />
                        <ButtonWithLoader
                            loading={loading}
                            type="submit"
                            variant="primary"
                            size="md"
                        >
                            Submit
                        </ButtonWithLoader>
                    </VStack>
                </Form>
            </FormikProvider>
        </section>
    );
};
